import { useState, useEffect } from "react";
import { API, INR } from "@/lib/api";
import { useCart } from "@/context/CartContext";
import { Plus } from "lucide-react";
import { toast } from "sonner";

const FALLBACK_IMG = {
  middle_eastern:
    "https://images.unsplash.com/photo-1737920459846-2d0318700658?crop=entropy&cs=srgb&fm=jpg&w=800&q=80",
  premium:
    "https://images.pexels.com/photos/12053223/pexels-photo-12053223.jpeg?auto=compress&cs=tinysrgb&dpr=2&w=800",
};

function resolveImage(product) {
  if (product.image_url && /^https?:/.test(product.image_url)) return product.image_url;
  if (product.has_image) return `${API}/products/${product.id}/image`;
  return FALLBACK_IMG[product.category] || FALLBACK_IMG.premium;
}

export default function ProductCard({ product, index = 0 }) {
  const stock = product.stock_ml ?? 0;
  const can5 = stock >= 5;
  const can10 = stock >= 10;
  const bothOut = !can5;
  const initialSize = can10 ? "10ml" : can5 ? "5ml" : "10ml";
  const [size, setSize] = useState(initialSize);
  const [imgLoaded, setImgLoaded] = useState(false);
  const { addItem, items } = useCart();

  // Keep selected size valid if stock/state changes
  useEffect(() => {
    if (size === "10ml" && !can10 && can5) setSize("5ml");
  }, [can5, can10, size]);

  // ml already reserved in the cart for this product
  const reservedMl = items
    .filter((i) => i.product_id === product.id)
    .reduce((s, i) => s + (i.size === "5ml" ? 5 : 10) * i.qty, 0);
  const remainingMl = Math.max(0, stock - reservedMl);
  const canAddCurrent = size === "5ml" ? remainingMl >= 5 : remainingMl >= 10;

  const price = size === "5ml" ? product.price_5ml : product.price_10ml;
  const img = resolveImage(product);
  const isMe = product.category === "middle_eastern";
  const badgeStyles = isMe
    ? "bg-[var(--cat-me-bg)] text-[var(--cat-me-text)] border-[var(--cat-me-border)]"
    : "bg-[var(--cat-pr-bg)] text-[var(--cat-pr-text)] border-[var(--cat-pr-border)]";

  const sizeDisabled = (s) => (s === "5ml" ? !can5 : !can10);

  const handleAdd = () => {
    if (!canAddCurrent) {
      toast.error(
        remainingMl === 0
          ? "Sold out"
          : `Only ${remainingMl}ml left — please remove items or pick a smaller size`
      );
      return;
    }
    addItem(product, size);
  };

  return (
    <div
      data-testid={`product-card-${product.id}`}
      className={`reveal group flex flex-col bg-[var(--bg-surface)] border border-[var(--border-default)] hover:border-[var(--border-hover)] transition-colors duration-300 ${bothOut ? "opacity-70" : ""}`}
      style={{ animationDelay: `${Math.min(index, 10) * 40}ms` }}
    >
      <div className="relative aspect-[4/5] bg-[var(--bg-elevated)] overflow-hidden">
        {!imgLoaded && (
          <div className="absolute inset-0 bg-gradient-to-br from-[var(--bg-elevated)] via-[#16181f] to-[var(--bg-elevated)] animate-pulse" />
        )}
        <img
          src={img}
          alt={`${product.brand} ${product.name}`}
          loading="lazy"
          decoding="async"
          onLoad={() => setImgLoaded(true)}
          onError={() => setImgLoaded(true)}
          className={`absolute inset-0 w-full h-full object-cover group-hover:scale-105 transition-all duration-700 ease-out ${imgLoaded ? "opacity-75 group-hover:opacity-100" : "opacity-0"}`}
        />
        <div className="absolute inset-0 bg-gradient-to-t from-black/50 via-transparent to-transparent pointer-events-none" />
        <div className="absolute top-3 left-3">
          <span
            data-testid={`product-category-${product.id}`}
            className={`px-2.5 py-1 text-[10px] uppercase tracking-wider font-medium border rounded-full ${badgeStyles}`}
          >
            {isMe ? "Middle Eastern" : "Premium"}
          </span>
        </div>
        {bothOut && (
          <div
            data-testid={`sold-out-overlay-${product.id}`}
            className="absolute inset-0 flex items-center justify-center bg-black/60 backdrop-blur-[2px]"
          >
            <span className="px-4 py-2 border border-red-400/60 text-red-300 font-headings text-xl tracking-[0.3em] uppercase">
              Sold Out
            </span>
          </div>
        )}
      </div>

      <div className="p-4 sm:p-5 flex flex-col gap-3 flex-grow">
        <div className="flex-grow">
          <div className="text-[10px] uppercase tracking-[0.2em] text-[var(--text-muted)] mb-1">
            {product.brand}
          </div>
          <h3 className="font-headings text-lg sm:text-xl text-[var(--text-primary)] leading-tight line-clamp-2">
            {product.name}
          </h3>
        </div>

        <div className="flex items-center justify-between">
          <div className="flex gap-1.5">
            {["5ml", "10ml"].map((s) => {
              const disabled = sizeDisabled(s);
              return (
                <button
                  key={s}
                  data-testid={`size-${s}-${product.id}`}
                  disabled={disabled}
                  onClick={() => !disabled && setSize(s)}
                  title={disabled ? `${s} unavailable` : ""}
                  className={`px-3 py-1.5 text-xs border transition-colors ${
                    disabled
                      ? "border-[var(--border-default)]/50 text-[var(--text-muted)] line-through cursor-not-allowed"
                      : size === s
                      ? "border-[var(--brand-gold)] text-[var(--brand-gold)] bg-[var(--brand-gold)]/10"
                      : "border-[var(--border-default)] text-[var(--text-secondary)] hover:border-[var(--border-hover)] hover:text-[var(--text-primary)]"
                  }`}
                >
                  {s}
                </button>
              );
            })}
          </div>
          <div className="font-headings text-lg text-[var(--brand-gold)] font-medium">
            {INR(price)}
          </div>
        </div>

        {!bothOut && stock > 0 && stock <= 20 && (
          <div
            data-testid={`low-stock-${product.id}`}
            className="text-[11px] text-amber-300/90"
          >
            Only {stock}ml left in stock
          </div>
        )}

        <button
          data-testid={`add-to-cart-${product.id}`}
          onClick={handleAdd}
          disabled={bothOut || !canAddCurrent}
          className="flex items-center justify-center gap-2 w-full mt-1 py-2.5 bg-[var(--brand-gold)] text-black text-sm font-medium tracking-wide hover:bg-[var(--brand-gold-hover)] transition-colors disabled:bg-[var(--border-default)] disabled:text-[var(--text-muted)] disabled:cursor-not-allowed"
        >
          <Plus size={16} />
          {bothOut
            ? "Sold Out"
            : !canAddCurrent
            ? "Not enough stock"
            : "Add to Cart"}
        </button>
      </div>
    </div>
  );
}
